import React from "react";
import { ShieldCheck, AlertCircle, PlaneLanding, PlaneTakeoff } from "lucide-react";

// Format a UTC minute-of-day (from the AIP MX window) into the airport's
// local wallclock when we know its tz_offset, otherwise fall back to UTC.
function fmtWindowMinute(min, offset) {
  if (min == null || !Number.isFinite(min)) return "—";
  let suffix = "Z";
  let v = min;
  if (Number.isFinite(offset)) {
    v = min + Math.round(offset * 60);
    suffix = "L";
  }
  v = ((v % 1440) + 1440) % 1440;
  const h = Math.floor(v / 60);
  const m = v % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}${suffix}`;
}

// Render the customs+immigration hours window as a condensed string.
function WindowValue({ w, offset, t }) {
  if (!w) return <span className="text-slate-500">—</span>;
  if (w.is_24h) return <span className="text-emerald-300 font-mono">H24</span>;
  if (w.is_unavailable)
    return <span className="text-rose-300">{t("hoursOfOperation.notPublished")}</span>;
  return (
    <span className="text-emerald-200 font-mono">
      {fmtWindowMinute(w.open_utc_min, offset)} → {fmtWindowMinute(w.close_utc_min, offset)}
    </span>
  );
}

// AIP México customs + immigration hours — shown only on the Mexican endpoint
// of an international leg (arrival into or departure from Mexico). Customs in
// Mexico is processed in BOTH directions, so the role label flips between
// "arrival" and "departure". Hours are published in UTC by SENEAM; we render
// them in the airport's local time when the offset is known.
export default function MxCustomsCard({ leg, t }) {
  const info = leg.mx_customs_info;
  if (!info) return null;

  const offset = info.tz_offset;
  const cust = info.customs_immigration_hours;
  const custSummer = info.customs_immigration_hours_summer;
  const isArrival = info.role === "destination";

  return (
    <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider text-emerald-400">
        <ShieldCheck className="w-3 h-3" />
        <span>
          {t("mxCustoms.badge")} · {info.icao}
        </span>
        {info.iata && (
          <span className="text-slate-500 font-mono normal-case tracking-normal">{info.iata}</span>
        )}
        <span className="inline-flex items-center gap-0.5 text-emerald-300/80 normal-case tracking-normal">
          {isArrival ? (
            <PlaneLanding className="w-2.5 h-2.5" />
          ) : (
            <PlaneTakeoff className="w-2.5 h-2.5" />
          )}
          {isArrival ? t("mxCustoms.arrival") : t("mxCustoms.departure")}
        </span>
      </div>

      <div className="text-[11px] text-slate-300 flex items-baseline gap-1.5">
        <span className="text-slate-400">{t("hoursOfOperation.customs")}:</span>
        <WindowValue w={cust} offset={offset} t={t} />
      </div>
      {custSummer && (
        <div className="text-[10px] text-slate-400 italic flex items-baseline gap-1.5 mt-0.5">
          <span>{t("hoursOfOperation.dstSummer")}:</span>
          <WindowValue w={custSummer} offset={offset} t={t} />
        </div>
      )}

      <p className="mt-2 text-[10px] text-amber-400/80 leading-snug flex items-start gap-1.5">
        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>{t("mxCustoms.disclaimer")}</span>
      </p>
    </div>
  );
}
